import React, { useEffect, useState, useCallback } from 'react';
import { CheckCircle2, XCircle, RefreshCw, Image as ImageIcon, Hash, Tag, Calendar, DollarSign } from 'lucide-react';
import apiClient from '@/lib/api';
import { toast } from 'sonner';

const STORE_ID = '52f68b0c-bb0b-41ee-9529-f6dda58e6d04';

const StatusPill = ({ ok, label, testId }) => (
  <span data-testid={testId}
    className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
      ok ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
    }`}>
    {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
    {label}
  </span>
);

const Row = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-start justify-between py-2 border-b border-slate-100 last:border-0">
    <div className="flex items-center gap-2 text-sm text-slate-600">
      {Icon && <Icon className="w-4 h-4 text-slate-400" />}
      <span>{label}</span>
    </div>
    <div data-testid={testId}
      className="text-sm font-medium text-slate-800 text-right break-all max-w-[60%]">
      {value ?? <span className="text-slate-400">—</span>}
    </div>
  </div>
);

const fmtMoney = (v) => (v == null ? '—' : `R$ ${Number(v).toFixed(2).replace('.', ',')}`);
const fmtDate = (s) => {
  if (!s) return null;
  try {
    const d = new Date(s);
    return d.toLocaleString('pt-BR');
  } catch { return s; }
};

export const IfoodProductConfig = ({ productId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchInfo = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/ifood/v2/${STORE_ID}/product/${productId}/ifood-info`);
      setData(res.data);
    } catch (err) {
      toast.error('Erro ao buscar info iFood: ' + (err?.response?.data?.detail || err.message));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => { fetchInfo(); }, [fetchInfo]);

  if (!productId) {
    return (
      <p className="text-sm text-slate-500" data-testid="ifood-cfg-no-product">
        Salve o produto primeiro para visualizar a integração com iFood.
      </p>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500" data-testid="ifood-cfg-loading">
        <RefreshCw className="w-4 h-4 animate-spin" />
        Carregando informações do iFood...
      </div>
    );
  }

  if (!data || !data.integrated) {
    return (
      <div className="text-sm" data-testid="ifood-cfg-not-integrated">
        <div className="flex items-center gap-2 text-yellow-700 bg-yellow-50 px-3 py-2 rounded-lg">
          <XCircle className="w-4 h-4" />
          <span>{data?.message || 'Produto não vinculado ao iFood V2'}</span>
        </div>
        <button
          type="button"
          onClick={fetchInfo}
          className="mt-2 inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
          data-testid="ifood-cfg-refresh-not-integrated"
        >
          <RefreshCw className="w-3 h-3" /> Tentar novamente
        </button>
      </div>
    );
  }

  const isSynced = ['SYNCED', 'SYNCED_EXISTING'].includes(data.sync_status);
  const isAvailable = data.ifood_status === 'AVAILABLE';
  const hasImage = !!data.image_path_ifood;

  return (
    <div className="space-y-3" data-testid="ifood-cfg-content">
      <div className="flex flex-wrap gap-2">
        <StatusPill ok={isSynced}
          label={isSynced ? 'SINCRONIZADO' : (data.sync_status || 'PENDENTE')}
          testId="ifood-cfg-sync-status" />
        <StatusPill ok={isAvailable}
          label={isAvailable ? 'DISPONÍVEL' : (data.ifood_status === 'UNAVAILABLE' ? 'INDISPONÍVEL' : (data.ifood_status || '—'))}
          testId="ifood-cfg-availability" />
        <StatusPill ok={hasImage}
          label={hasImage ? 'IMAGEM ENVIADA' : 'SEM IMAGEM'}
          testId="ifood-cfg-image-status" />
      </div>

      <div className="bg-white rounded-lg border border-slate-200 px-4 py-2">
        <Row icon={DollarSign} label="Preço no iFood"
             value={fmtMoney(data.ifood_price)} testId="ifood-cfg-price" />
        <Row icon={Calendar} label="Última sincronização"
             value={fmtDate(data.last_sync_at)} testId="ifood-cfg-last-sync" />
        <Row icon={Tag} label="Código externo"
             value={<code className="font-mono text-xs">{data.external_code}</code>}
             testId="ifood-cfg-external-code" />
        <Row icon={Hash} label="ID do Item iFood"
             value={<code className="font-mono text-xs">{data.item_id}</code>}
             testId="ifood-cfg-item-id" />
        <Row icon={Hash} label="Item Context ID"
             value={data.item_context_id ? <code className="font-mono text-xs">{data.item_context_id}</code> : null}
             testId="ifood-cfg-item-context-id" />
        <Row icon={Hash} label="Product ID iFood"
             value={data.product_id_ifood ? <code className="font-mono text-xs">{data.product_id_ifood}</code> : null}
             testId="ifood-cfg-product-id-ifood" />
        <Row icon={ImageIcon} label="Caminho da imagem"
             value={data.image_path_ifood ? <code className="font-mono text-xs">{data.image_path_ifood}</code> : null}
             testId="ifood-cfg-image-path" />
        <Row icon={Tag} label="Catálogo"
             value={data.catalog_id ? <code className="font-mono text-xs">{data.catalog_id}</code> : null}
             testId="ifood-cfg-catalog-id" />
        <Row icon={Tag} label="Categoria iFood"
             value={data.category_id_ifood ? <code className="font-mono text-xs">{data.category_id_ifood}</code> : null}
             testId="ifood-cfg-category-id" />
      </div>

      <button
        type="button"
        onClick={fetchInfo}
        className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
        data-testid="ifood-cfg-refresh"
      >
        <RefreshCw className="w-3 h-3" /> Atualizar
      </button>
    </div>
  );
};

export default IfoodProductConfig;
